import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar } from "recharts";
import { adminDashboard, couriers } from "../../services/apiClient";
import StatCard from "../components/StatCard";
import PageHeader from "../components/PageHeader";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import { useAdminPeriod } from "../../contexts/AdminPeriodContext";

export default function AdminDashboard() {
  const { period } = useAdminPeriod();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [courierRows, setCourierRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [data, couriersData] = await Promise.all([
          adminDashboard.getSummary(period),
          couriers.list(),
        ]);
        if (mounted) {
          setSummary(data);
          setCourierRows(Array.isArray(couriersData) ? couriersData : []);
        }
      } catch (err) {
        if (mounted) setError("Nao foi possivel carregar o dashboard.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [period]);

  const topOpenStores = useMemo(() => (summary?.open_stores || []).slice(0, 5), [summary]);
  const topClosedStores = useMemo(() => (summary?.closed_stores || []).slice(0, 5), [summary]);
  const topOnlineCouriers = useMemo(() => (summary?.online_couriers || []).slice(0, 5), [summary]);
  const topStoresByGmv = useMemo(() => (summary?.top_stores_gmv || []).slice(0, 5), [summary]);
  const topStoresByOrders = useMemo(() => (summary?.top_stores_orders || []).slice(0, 5), [summary]);
  const courierOverview = useMemo(() => {
    return (Array.isArray(courierRows) ? courierRows : []).reduce(
      (acc, row) => {
        const financial = row?.financial && typeof row.financial === "object" ? row.financial : {};
        const completed = Number(row?.deliveries_completed_total ?? financial?.deliveries_completed_total ?? 0);
        const unpaidDeliveries = Number(financial?.unpaid_deliveries_count ?? 0);
        const readyForPayout = Number(row?.receivable_cents ?? financial?.ready_for_payout_cents ?? financial?.receivable_cents ?? 0);
        const totalAdminFee = Number(row?.total_admin_fee_cents ?? financial?.total_admin_fee_cents ?? 0);

        acc.completedDeliveries += Number.isFinite(completed) ? Math.max(0, completed) : 0;
        acc.unpaidDeliveries += Number.isFinite(unpaidDeliveries) ? Math.max(0, unpaidDeliveries) : 0;
        acc.readyForPayoutCents += Number.isFinite(readyForPayout) ? Math.max(0, readyForPayout) : 0;
        acc.adminFeeTotalCents += Number.isFinite(totalAdminFee) ? Math.max(0, totalAdminFee) : 0;
        return acc;
      },
      {
        completedDeliveries: 0,
        unpaidDeliveries: 0,
        readyForPayoutCents: 0,
        adminFeeTotalCents: 0,
      }
    );
  }, [courierRows]);

  const formatAvailability = (seconds) => {
    if (seconds == null) return "N/A";
    const totalMinutes = Math.floor(seconds / 60);
    if (totalMinutes < 60) return `${totalMinutes} min`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}min`;
  };
  const formatMoneyFromCents = (value) => `R$ ${(Math.max(0, Number(value) || 0) / 100).toFixed(2)}`;

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!summary) return null;

  return (
    <div className="admin-section">
      <PageHeader title="Visao Geral" subtitle="Indicadores principais do sistema" />

      <div className="admin-grid">
        <StatCard
          title="GMV"
          value={`R$ ${summary.gmv.toFixed(2)}`}
          subtitle={`${summary.orders} pedidos`}
          onClick={() => navigate("/admin/financeiro")}
          ariaLabel="Ver detalhes de GMV no financeiro"
        />
        <StatCard
          title="Receita Admin"
          value={`R$ ${summary.admin_revenue.toFixed(2)}`}
          subtitle={`Taxa media ${summary.fee_percent}%`}
          onClick={() => navigate("/admin/financeiro")}
          ariaLabel="Ver detalhes da receita admin no financeiro"
        />
        <StatCard
          title="Ticket Medio"
          value={`R$ ${summary.avg_ticket.toFixed(2)}`}
          subtitle={`Periodo: ${period}`}
          onClick={() => navigate("/admin/pedidos")}
          ariaLabel="Ver detalhes de ticket medio na tela de pedidos"
        />
        <StatCard
          title="Motoboys Online"
          value={summary.couriers_online}
          subtitle="Agora"
          onClick={() => navigate("/admin/motoboys")}
          ariaLabel="Ver motoboys online"
        />
        <StatCard
          title="Corridas motoboys"
          value={Math.round(courierOverview.completedDeliveries)}
          subtitle={`${Math.round(courierOverview.unpaidDeliveries)} em aberto`}
          onClick={() => navigate("/admin/motoboys")}
          ariaLabel="Ver resumo de corridas dos motoboys"
        />
        <StatCard
          title="A receber motoboys"
          value={formatMoneyFromCents(courierOverview.readyForPayoutCents)}
          subtitle="Pronto para programar pagamento"
          onClick={() => navigate("/admin/motoboys")}
          ariaLabel="Ver valores a receber dos motoboys"
        />
        <StatCard
          title="Taxa ADM motoboy"
          value={formatMoneyFromCents(courierOverview.adminFeeTotalCents)}
          subtitle={`Desconto em % das corridas: ${summary.fee_percent}%`}
          onClick={() => navigate("/admin/taxas-regras")}
          ariaLabel="Ver regra de desconto das corridas dos motoboys"
        />
        <StatCard
          title="Envios e Viagens"
          value="Servico ativo"
          subtitle="Configurar regra da corrida"
          onClick={() => navigate("/admin/taxas-regras?section=envios-viagens")}
          ariaLabel="Abrir configuracao de Envios e Viagens"
        />
      </div>

      <div className="admin-grid admin-grid-charts">
        <div
          className="admin-card clickable"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/admin/pedidos")}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigate("/admin/pedidos");
            }
          }}
        >
          <h3>Ticket medio por periodo</h3>
          <div className="admin-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={summary.ticket_series}
                margin={{ top: 8, right: 12, left: -12, bottom: 8 }}
              >
                <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                <XAxis
                  dataKey="label"
                  stroke="#B8B8C5"
                  tick={{ fill: "#B8B8C5", fontSize: 12 }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis stroke="#B8B8C5" tick={{ fill: "#B8B8C5", fontSize: 12 }} width={36} />
                <Tooltip
                  contentStyle={{ background: "#12121A", border: "1px solid #2A2A3A", borderRadius: 10, color: "#fff" }}
                  labelStyle={{ color: "#fff" }}
                  cursor={{ stroke: "rgba(255,255,255,0.2)" }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#FF6A00"
                  strokeWidth={3}
                  dot={{ r: 3, strokeWidth: 1, fill: "#FF6A00", stroke: "#fff" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          className="admin-card clickable"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/admin/pedidos")}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigate("/admin/pedidos");
            }
          }}
        >
          <h3>Pedidos por periodo</h3>
          <div className="admin-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={summary.orders_series}
                margin={{ top: 8, right: 12, left: -12, bottom: 8 }}
              >
                <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                <XAxis
                  dataKey="label"
                  stroke="#B8B8C5"
                  tick={{ fill: "#B8B8C5", fontSize: 12 }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis stroke="#B8B8C5" tick={{ fill: "#B8B8C5", fontSize: 12 }} width={36} />
                <Tooltip
                  contentStyle={{ background: "#12121A", border: "1px solid #2A2A3A", borderRadius: 10, color: "#fff" }}
                  labelStyle={{ color: "#fff" }}
                  cursor={{ fill: "rgba(255,255,255,0.06)" }}
                />
                <Bar dataKey="value" fill="#FF6A00" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="admin-grid">
        <div
          className="admin-card clickable"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/admin/lojas?status=open")}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigate("/admin/lojas?status=open");
            }
          }}
        >
          <h3>Lojas abertas agora</h3>
          <ul className="admin-list">
            {topOpenStores.map((store) => (
              <li key={store.id}>
                {store.name}
                {store.duration_seconds != null ? ` - ${formatAvailability(store.duration_seconds)}` : ""}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="admin-card clickable"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/admin/lojas?status=closed")}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigate("/admin/lojas?status=closed");
            }
          }}
        >
          <h3>Lojas fechadas agora</h3>
          <ul className="admin-list">
            {topClosedStores.map((store) => (
              <li key={store.id}>
                {store.name}
                {store.duration_seconds != null ? ` - ${formatAvailability(store.duration_seconds)}` : ""}
              </li>
            ))}
          </ul>
        </div>

        <div className="admin-card clickable" role="button" tabIndex={0} onClick={() => navigate("/admin/motoboys")} onKeyDown={(event) => { if (event.key === "Enter") navigate("/admin/motoboys"); }}>
          <h3>Motoboys online</h3>
          <ul className="admin-list">
            {topOnlineCouriers.length === 0 ? (
              <li>Nenhum online agora</li>
            ) : (
              topOnlineCouriers.map((courier) => (
                <li key={courier.id}>
                  {courier.name}
                  {courier.available_seconds != null ? ` - ${formatAvailability(courier.available_seconds)}` : ""}
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      <div className="admin-grid">
        <div className="admin-card clickable" role="button" tabIndex={0} onClick={() => navigate("/admin/lojas")} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); navigate("/admin/lojas"); } }}>
          <h3>Top lojas por GMV</h3>
          <ul className="admin-list">
            {topStoresByGmv.map((store) => (
              <li
                key={store.id}
                className="admin-list-item-clickable"
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/admin/lojas/${store.id}`);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    navigate(`/admin/lojas/${store.id}`);
                  }
                }}
              >
                {store.name} - R$ {store.value.toFixed ? store.value.toFixed(2) : store.value}
              </li>
            ))}
          </ul>
        </div>

        <div className="admin-card clickable" role="button" tabIndex={0} onClick={() => navigate("/admin/pedidos")} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); navigate("/admin/pedidos"); } }}>
          <h3>Top lojas por pedidos</h3>
          <ul className="admin-list">
            {topStoresByOrders.map((store) => (
              <li
                key={store.id}
                className="admin-list-item-clickable"
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/admin/pedidos?store_id=${store.id}&store_name=${encodeURIComponent(store.name)}`);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    navigate(`/admin/pedidos?store_id=${store.id}&store_name=${encodeURIComponent(store.name)}`);
                  }
                }}
              >
                {store.name} - {store.value} pedidos
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
